/**
 * GEDCOM INDI Parser
 *
 * Extracts and parses individual (INDI) records from GEDCOM tree.
 * Handles NAME, SEX, BIRT, and DEAT tags.
 *
 * Part of Phase 4 - Slice 2 & 4: Parser Integration + Date Parsing
 */

import type { TreeNode } from '../types/TreeNode';
import { parseGedcomName } from './nameParser';
import { parseGedcomDate, type DateQualifier } from './dateParser';

/**
 * Parsed person data from INDI record
 */
export interface ParsedPerson {
  /** GEDCOM identifier (e.g., "@I1@") */
  gedcomId: string;

  /** Given name(s) / first name(s) */
  givenName: string;

  /** Surname / family name (null if not present) */
  surname: string | null;

  /** Formatted display name for UI */
  displayName: string;

  /** Normalized gender */
  gender: 'male' | 'female' | 'other';

  /** Birth date in ISO format (YYYY-MM-DD), null if not available */
  birthDate: string | null;

  /** Birth year (extracted from birth date) */
  birthYear: number | null;

  /** Birth date qualifier (ABT, BEF, AFT, CAL, EST) */
  birthQualifier: DateQualifier | null;

  /** Death date in ISO format (YYYY-MM-DD), null if not available */
  deathDate: string | null;

  /** Death year (extracted from death date) */
  deathYear: number | null;

  /** Death date qualifier (ABT, BEF, AFT, CAL, EST) */
  deathQualifier: DateQualifier | null;
}

/**
 * Extract and parse all INDI records from GEDCOM tree
 *
 * @param tree - Root nodes of GEDCOM tree
 * @returns Array of parsed person records
 */
export const extractIndiRecords = (tree: TreeNode[]): ParsedPerson[] => {
  const persons: ParsedPerson[] = [];

  // Find all INDI record nodes
  const indiNodes = tree.filter((node) => node.tag === 'INDI' && node.pointer !== null);

  indiNodes.forEach((indiNode) => {
    try {
      const person = parseIndiNode(indiNode);
      if (person) {
        persons.push(person);
      }
    } catch (error) {
      console.warn(
        `[GEDCOM Parser] Failed to parse INDI record ${indiNode.pointer} at line ${indiNode.lineNumber}:`,
        error
      );
    }
  });

  return persons;
};

/**
 * Parse a single INDI node into ParsedPerson
 *
 * @param indiNode - INDI TreeNode
 * @returns ParsedPerson or null if required data is missing
 */
const parseIndiNode = (indiNode: TreeNode): ParsedPerson | null => {
  if (!indiNode.pointer) {
    console.warn(`[GEDCOM Parser] INDI node at line ${indiNode.lineNumber} has no pointer`);
    return null;
  }

  const gedcomId = indiNode.pointer;

  // Find NAME child
  const nameNode = indiNode.children.find((child) => child.tag === 'NAME');
  const parsedName = parseGedcomName(nameNode?.value || null);

  // Find SEX child
  const sexNode = indiNode.children.find((child) => child.tag === 'SEX');
  const gender = normalizeSex(sexNode?.value || null);

  // Find BIRT child and extract DATE
  const birthNode = indiNode.children.find((child) => child.tag === 'BIRT');
  const birthDateNode = birthNode?.children.find((child) => child.tag === 'DATE');
  const birthParsed = parseGedcomDate(birthDateNode?.value);

  // Find DEAT child and extract DATE
  const deathNode = indiNode.children.find((child) => child.tag === 'DEAT');
  const deathDateNode = deathNode?.children.find((child) => child.tag === 'DATE');
  const deathParsed = parseGedcomDate(deathDateNode?.value);

  return {
    gedcomId,
    givenName: parsedName.givenName,
    surname: parsedName.surname,
    displayName: parsedName.displayName,
    gender,
    birthDate: birthParsed?.date || null,
    birthYear: birthParsed?.year || null,
    birthQualifier: birthParsed?.qualifier || null,
    deathDate: deathParsed?.date || null,
    deathYear: deathParsed?.year || null,
    deathQualifier: deathParsed?.qualifier || null,
  };
};

/**
 * Normalize GEDCOM SEX value to gender type
 *
 * GEDCOM spec:
 * - M = Male
 * - F = Female
 * - U = Unknown/Undetermined
 * - Missing = Unknown
 *
 * @param sexValue - Raw SEX tag value
 * @returns Normalized gender
 */
export const normalizeSex = (sexValue: string | null): 'male' | 'female' | 'other' => {
  if (!sexValue) {
    return 'other';
  }

  const normalized = sexValue.trim().toUpperCase();

  switch (normalized) {
    case 'M':
    case 'MALE':
      return 'male';

    case 'F':
    case 'FEMALE':
      return 'female';

    case 'U':
    case 'UNKNOWN':
    case 'OTHER':
      return 'other';

    default:
      console.warn(`[GEDCOM Parser] Unknown SEX value: "${sexValue}". Treating as 'other'.`);
      return 'other';
  }
};
